import { countries as countryData, TCountryCode } from 'countries-list';

interface ProfileViewOptions {
  countries: { id: TCountryCode; label: string }[];
  roles: { id: string; label: string }[];
  yearsInIndustry: { id: string; label: string }[];
  sizeUnits: { id: string; label: string }[];
  farmSizesHectares: { id: string; label: string }[];
  farmSizesAcres: { id: string; label: string }[];
  companySizes: { id: string; label: string }[];
  livestockOptions: { id: string; label: string }[];
}

export const createProfileViewOptions = (locale: string): ProfileViewOptions => {
  const displayNames = new Intl.DisplayNames(locale, { type: 'region' });

  return {
    countries: Object.keys(countryData)
      .map((code) => ({
        id: code as TCountryCode,
        label: displayNames.of(code) ?? countryData[code as TCountryCode].name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label, locale)),
    roles: [
      { id: 'farm-owner', label: $localize`Farm owner` },
      { id: 'farm-worker', label: $localize`Farm worker` },
      { id: 'contractor-owner', label: $localize`Contractor, owner` },
      { id: 'contractor-worker', label: $localize`Contractor, worker` },
      { id: 'dealer', label: $localize`Dealer` },
      { id: 'consultant', label: $localize`Agronomist, advisor or consultant` },
      { id: 'student', label: $localize`Student` },
      { id: 'other', label: $localize`Other` },
    ],
    yearsInIndustry: [
      { id: '0-4', label: $localize`Less than 5 years` },
      { id: '5-9', label: $localize`5-9 years` },
      { id: '10-19', label: $localize`10-19 years` },
      { id: '19+', label: $localize`More than 19 years` },
    ],
    sizeUnits: [
      { id: 'ha', label: $localize`Hectares` },
      { id: 'ac', label: $localize`Acres` },
    ],
    farmSizesHectares: [
      { id: '0-49', label: $localize`Less than 50 hectares` },
      { id: '50-99', label: $localize`50-99 hectares` },
      { id: '100-299', label: $localize`100-299 hectares` },
      { id: '300-599', label: $localize`300-599 hectares` },
      { id: '600-999', label: $localize`600-999 hectares` },
      { id: '1000-1500', label: $localize`1000-1500 hectares` },
      { id: '1500+', label: $localize`More than 1500 hectares` },
    ],
    farmSizesAcres: [
      { id: '0-124', label: $localize`Less than 125 acres` },
      { id: '125-244', label: $localize`125-244 acres` },
      { id: '245-739', label: $localize`245-739 acres` },
      { id: '740-1479', label: $localize`740-1479 acres` },
      { id: '1480-2469', label: $localize`1480-2469 acres` },
      { id: '2470-3700', label: $localize`2470-3700 acres` },
      { id: '3700+', label: $localize`More than 3700 acres` },
    ],
    companySizes: [
      { id: '0-4', label: $localize`Less than 5 employees` },
      { id: '5-19', label: $localize`5-19 employees` },
      { id: '20-49', label: $localize`20-49 employees` },
      { id: '50-99', label: $localize`50-99 employees` },
      { id: '99+', label: $localize`More than 99 employees` },
    ],
    livestockOptions: [
      { id: 'none', label: $localize`No livestock` },
      { id: 'cows-milk', label: $localize`Cows, milk production` },
      { id: 'cows-meat', label: $localize`Cows, meat production` },
      { id: 'pigs', label: $localize`Pigs` },
      { id: 'chicken', label: $localize`Hen / chicken` },
      { id: 'horses', label: $localize`Horses` },
      { id: 'other', label: $localize`Other` },
    ],
  };
};
